import { getMilkReportYears, summarizeYearMilk } from "./year-milk-production.js";

export const reportDate = (value) => value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString("el-GR", { timeZone: "Europe/Athens" }) : "";
const ownerHeaders = ["Κωδικός Μονάδας", "Παραγωγός"];
const ownerValues = (owner) => [owner?.code ?? "", [owner?.lastName, owner?.firstName].filter(Boolean).join(" ")];
const sortedMonthly = (data) => [...data].sort((a, b) => !isNaN(a.registrationCode) && !isNaN(b.registrationCode)
    ? Number(a.registrationCode) - Number(b.registrationCode) : String(a.registrationCode).localeCompare(String(b.registrationCode)));

export const monthlyMilkCsv = (owner, year, month, data = []) => [
    [...ownerHeaders, "Έτος", "Μήνας", "Α/Α", "Κωδικός Ζώου", "Α/Α Ελέγχου", "Ημερομηνία Τοκετού",
        "Πρωί - Γάλα (γρ)", "Μεσημέρι - Γάλα (γρ)", "Βράδυ - Γάλα (γρ)", "Λίπος (%)", "Πρωτεΐνη (%)", "Λακτόζη (%)", "Ημερομηνία Ελέγχου"],
    ...sortedMonthly(data).map((record, index) => [...ownerValues(owner), year, month, index + 1, record.registrationCode,
        record.milkRecordingCount, reportDate(record.lambingDate),
        ...["Πρωί", "Μεσημέρι", "Βράδυ"].map((type) => record.milkProduced?.find((entry) => entry.type === type)?.milk ?? null),
        record.fat, record.protein, record.lactose, reportDate(record.testDate)]),
];

export const ownerPopulationCsv = (owner, year, alive = [], dead = []) => [
    [...ownerHeaders, "Έτος", "Κατηγορία", "Α/Α", "Γενεαλογικός Κωδικός", "Κωδικός στην Εκτροφή", "Ημερομηνία Γέννησης",
        "Φύλο", "Ημερομηνία Εγγραφής", "Φυλή", "Πατέρας", "Μητέρα"],
    ...[["Παρόντα Ζώα", alive], ["Νεκρά Ζώα", dead]].flatMap(([category, animals]) => animals.map((record, index) => [
        ...ownerValues(owner), year, category, index + 1, record.registrationCode, record.flockCode, reportDate(record.birthDate),
        record.sex, reportDate(record.registrationDate), record.breedCode,
        record.sireCode === "Unknown" ? "" : record.sireCode, record.damCode === "Unknown" ? "" : record.damCode,
    ])),
];

export const detailedPopulationCsv = (owner, year, title, data = []) => {
    const isLamb = title && (title.includes("αμνο") || title.includes("Αμνο"));
    const extras = title === "Στέρφα" ? [["Τελευταία Ημερομηνία Γέννας", (r) => reportDate(r.lastLambingDate)]]
        : ["Θηλυκά Ζώα με Ηλικία > 12 μήνες", "Θηλυκά Ζώα με Ηλικία > 14 μήνες"].includes(title)
            ? [["Ηλικία (μήνες)", (r) => r.ageInMonths]]
            : title === "Προβατίνες σε γαλακτοπαραγωγή"
                ? [["Ημερομηνία Γέννας", (r) => reportDate(r.lambingDate)], ["Τρέχουσα Γαλακτική Περίοδος", (r) => r.milkingPeriod]] : [];
    const sorted = title === "Προβατίνες σε γαλακτοπαραγωγή" && data[0]?.milkingPeriod
        ? [...data].sort((a, b) => b.milkingPeriod - a.milkingPeriod) : data;
    return [
        [...ownerHeaders, "Έτος αναφοράς", "Κατηγορία", "Α/Α", ...(isLamb
            ? ["Κωδικός Αμνού", "Φύλο", "Γεν. Κωδικός Μητέρας", "Ημερομηνία Γέννησης", "Κατάσταση"]
            : ["Γενεαλογικός Κωδικός", "Κωδικός στην Εκτροφή", "Ημερομηνία Γέννησης", ...extras.map(([header]) => header)])],
        ...sorted.map((r, index) => [...ownerValues(owner), year, title, index + 1, ...(isLamb
            ? [r.lambSlug, r.sex, r.motherRegistrationCode, reportDate(r.birthDate), r.statusGreek]
            : [r.registrationCode, r.flockCode, reportDate(r.birthDate), ...extras.map(([, value]) => value(r))])]),
    ];
};

const calculationStatuses = {
    stale: "Χρειάζεται επανυπολογισμός", unavailable: "Μη διαθέσιμος υπολογισμός",
    "not-calculated": "Δεν έχει γίνει υπολογισμός", partial: "Μερική παραγωγή", legacy: "Ιστορικό αποτέλεσμα", current: "Υπολογισμένο",
};
const notes = (records, summary) => [...new Set([
    ...records.filter((r) => ["stale", "unavailable", "not-calculated"].includes(r.calculationStatus))
        .map((r) => calculationStatuses[r.calculationStatus]),
    ...(summary.ongoing ? ["Περιλαμβάνει ανοικτή περίοδο"] : []),
    ...(summary.partial ? ["Μερική παραγωγή"] : []),
    ...(summary.predicted ? ["Πρόβλεψη τυπικής παραγωγής"] : []),
])].join(" | ");
const annualAnimals = (data, years) => data.filter((animal) => years.some((year) => animal.yearlyProduction?.[year]?.length)
    || animal.unassignedPeriods?.length).sort((a, b) => String(a.registrationCode || "").localeCompare(String(b.registrationCode || "")));

export const yearlyMilkCsv = (owner, category, data = [], years = getMilkReportYears()) => [
    [...ownerHeaders, "Κατηγορία ζώων", "Κωδικός Γενεαλογικού Βιβλίου", ...years.flatMap((year) => [
        `Πραγματική παραγωγή ${year} (kg)`, `Τυπική παραγωγή ${year} (kg)`, `Μέρες γαλακτοπαραγωγής ${year}`, `Παρατηρήσεις ${year}`,
    ]), "Περίοδοι χωρίς καταχωρημένο τοκετό"],
    ...annualAnimals(data, years).map((animal) => [...ownerValues(owner), category, animal.registrationCode,
        ...years.flatMap((year) => {
            const records = animal.yearlyProduction?.[year] || [];
            const summary = summarizeYearMilk(records);
            return [summary.milk, summary.standardMilk, summary.duration, notes(records, summary)];
        }), animal.unassignedPeriods?.length || 0]),
];

export const yearlyMilkPeriodsCsv = (owner, category, data = [], years = getMilkReportYears()) => [
    [...ownerHeaders, "Κατηγορία ζώων", "Κωδικός Γενεαλογικού Βιβλίου", "Έτος γαλακτοπαραγωγής", "Ημερομηνία Τοκετού",
        "Πραγματική παραγωγή (kg)", "Τυπική παραγωγή (kg)", "Μέρες γαλακτοπαραγωγής", "Κατάσταση υπολογισμού", "Κατάσταση περιόδου",
        "Πρόβλεψη τυπικής παραγωγής", "Ημερομηνία υπολογισμού"],
    ...annualAnimals(data, years).flatMap((animal) => [
        ...years.flatMap((year) => (animal.yearlyProduction?.[year] || []).map((r) => ({ ...r, year }))),
        ...(animal.unassignedPeriods || []).map((r) => ({ ...r, year: "Άγνωστο έτος - λείπει ο τοκετός" })),
    ].map((r) => {
        const summary = summarizeYearMilk([r]);
        return [...ownerValues(owner), category, animal.registrationCode, r.year, reportDate(r.lambingDate),
            summary.milk, summary.standardMilk, summary.duration, calculationStatuses[r.calculationStatus] || "Μη καταγεγραμμένη",
            ({ closed: "Έχει λήξει", ongoing: "Σε εξέλιξη" })[r.periodState] || "Άγνωστη",
            summary.predicted ? "Ναι" : "Όχι", reportDate(r.calculatedAt)];
    })),
];
